import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ButtonDirective} from "primeng/button";
import {TooltipModule} from "primeng/tooltip";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {IconPathPipe} from "@app/utils/pipes/icon-path-pipe";
import {GeoJSONGeometry} from "@components/request-map/geo-json-geometry.interface";
import {environment} from '@root/src/environments/environment';
import {getBorderColorFromPolygonColor} from '@app/utils/_msf';

declare var ol: any;


export interface IPolygonColor {
  polygon: string,
  color?: string
}

@Component({
  selector: 'mfs-request-openlayers-map-2',
  standalone: true,
  imports: [CommonModule, ButtonDirective, TooltipModule, TranslateModule, IconPathPipe],
  templateUrl: './request-map.component.html',
  styleUrls: ['./request-map.component.scss'],
})
export class RequestMapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapContainer', {static: true}) mapContainer!: ElementRef;

  private map: any;
  private vectorSource: any;
  private vectorLayer: any;
  private centerMarker: any;
  public satelliteLayer: any;
  private drawInteraction: any;
  private isDrawing = false;
  private ndiLayer: any;
  private isDrawingLineString!: boolean;
  private isDrawingPolygon!: boolean;
  public mapLoaded = false;
  private modifyInteraction: any;

  private _polygonJson: string | undefined = undefined;
  private _multiPolygonJson: string[] = [];

  @Input() height = '100%';
  @Input() center: [any?, any?] = [environment.map.lng, environment.map.lat];
  @Input() zoom = 13;
  @Input() drawingMode = false;
  @Input() satellite: boolean = false;
  @Input() isCenterMarker: boolean = true;
  @Input() showCenterMarkerOnInit: boolean = true;
  @Input() polygonArea: number = 0;
  @Input() polygonColor = "rgba(39, 237, 0, 0.44)"

  @Input() drawingOptions =  {
    polygon:true,
    point:true ,
  }


  @Output() polygonJsonChange = new EventEmitter<any>();
  @Output() polygonAreaChange = new EventEmitter<any>();


  @Input() set polygonJson(json: string | undefined) {
    this._polygonJson = json;
    if (json && this.mapLoaded) {
      this.removePolygonLayer();
      setTimeout(() => this.drawPolygonFromJson(json), 500);
    }
  }

  @Input() set multiPolygonJson(json: string[] | undefined) {
    if (json && this.mapLoaded) {
      setTimeout(() => {
        this._multiPolygonJson = [];
        json.forEach(j => this.drawPolygonFromJson(j));
      }, 500);
    }
  }

  @Input() set multiPolygonJsonColors(json: IPolygonColor[] | undefined) {
    if (json && this.mapLoaded) {
      setTimeout(() => {
        this._multiPolygonJson = [];
        json.forEach(j => this.drawPolygonFromJson(j.polygon, j.color));
      }, 500);
    }
  }

  @Input() geometry: any;
  @Input() showControls = true;
  @Output() coordinatesChange = new EventEmitter<{ lat: number; lng: number }[]>();
  @Output() markerClick = new EventEmitter<{ lat: number; lng: number }>();
  @Output() mapClick = new EventEmitter<{ lat: number; lng: number }>();
  @Output() mapMove = new EventEmitter<any>();
  @Output() geometryChange = new EventEmitter<any>();


  ngOnInit() {
    if (!document.querySelector('link[href*="ol.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/gh/openlayers/openlayers.github.io@master/en/v6.15.1/css/ol.css';
      document.head.appendChild(link);
    }

    if (typeof ol === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/openlayers/openlayers.github.io@master/en/v6.15.1/build/ol.js';
      script.onload = () => {
        console.log('OpenLayers loaded successfully');
        this.initializeMap();
      };
      script.onerror = () => {
        console.error('Failed to load OpenLayers');
      };
      document.body.appendChild(script);
    }
  }

  ngAfterViewInit() {
    if (typeof ol !== 'undefined') {
      setTimeout(() => this.initializeMap(), 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['center'] && this.map && !changes['center'].firstChange) {
      const coords = changes['center'].currentValue as [number, number];
      this.updateCenterMarker(coords[0], coords[1]);
    }
    if (changes['polygonColor'] && this.vectorSource && !changes['polygonColor'].firstChange) {
      const borderColor = getBorderColorFromPolygonColor(this.polygonColor);
      this.vectorSource.getFeatures().forEach((feature: any) => {
        if (feature.getGeometry().getType() === 'Polygon') {
          feature.setStyle(new ol.style.Style({
            stroke: new ol.style.Stroke({color: borderColor, width: 2}),
            fill: new ol.style.Fill({color: this.polygonColor})
          }));
        }
      });
    }
  }


  intVector() {
    this.vectorSource = new ol.source.Vector();
    this.vectorLayer = new ol.layer.Vector({
      source: this.vectorSource,
      style: new ol.style.Style({
        fill: new ol.style.Fill({
          color: 'rgba(119, 255, 15, 0.34)'
        }),
        stroke: new ol.style.Stroke({
          color: '#ff4b4b',
          width: 2
        }),
        image: new ol.style.Circle({
          radius: 5,
          fill: new ol.style.Fill({
            color: '#ff0000'
          }),
          stroke: new ol.style.Stroke({
            color: '#ffffff',
            width: 2
          })
        })
      })
    });
  }

  initMapOnclick() {
    this.map.on('click', (evt: any) => {
      const coordinate = evt.coordinate;
      const lonLat = ol.proj.toLonLat(coordinate);

      if (this.drawingMode) {
        if (!this.isDrawing) {
          if (this.isDrawingPolygon) {
          } else if (this.isDrawingLineString) {
          }
          this.isDrawing = true;
        }

      } else {
        this.markerClick.emit({lat: lonLat[1], lng: lonLat[0]});
      }
    });
  }

  drawPolygonAfterMapLoad() {
    if (this._polygonJson) {
      this.drawPolygonFromJson(this._polygonJson);
    }

    if (this._multiPolygonJson.length > 0) {
      this._multiPolygonJson.forEach(j => this.drawPolygonFromJson(j));
    }


    this.enableEditPolygon();
  }

  private initializeMap() {

    if (!this.mapContainer || this.map) return;

    try {

      if (typeof ol === 'undefined') {
        return;
      }

      this.intVector()


      const osmLayer = new ol.layer.Tile({
        source: new ol.source.XYZ({
          url: 'https://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
          attributions: '© Esri'
        }),
        opacity: 1
      });


      this.map = new ol.Map({
        target: this.mapContainer.nativeElement,
        layers: [osmLayer, this.vectorLayer],
        view: new ol.View({
          center: ol.proj.fromLonLat(this.center),
          zoom: this.zoom
        }),
        controls: ol.control.defaults({
          attribution: false,
          zoom: true
        })
      });


      this.initMapOnclick();


      if (this.center && this.center[0] !== undefined && this.center[1] !== undefined) {
        //@ts-ignore
        this.updateCenterMarker(this.center[0], this.center[1]);
      }

      this.map.on('moveend', () => {
      });
      this.mapLoaded = true;
      this.drawPolygonAfterMapLoad();
    } catch (error) {
      console.error('Error initializing OpenLayers map:', error);
    }

    if (this.satellite) {
      this.addSatelliteLayer();
    }
  }


  public startPolygonDrawing() {
    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
    }

    this.drawInteraction = new ol.interaction.Draw({
      source: this.vectorSource,
      type: 'Polygon'
    });

    this.drawingMode = true;

    this.drawInteraction.on('drawstart', (event: any) => {
      const polygon = event.feature;
      polygon.set('type', 'polygon');

      polygon.getGeometry().on('change', (geomEvt: any) => {
        const coords = geomEvt.target.getCoordinates()[0];

        this.vectorSource.getFeatures()
          .filter((f: any) => f.get('type') === 'vertex')
          .forEach((f: any) => this.vectorSource.removeFeature(f));

        coords.forEach((coord: any) => {
          const pointFeature = new ol.Feature({
            geometry: new ol.geom.Point(coord),
            type: 'vertex'
          });
          this.vectorSource.addFeature(pointFeature);
        });

        const latlngs = coords.map((c: any) => {
          const lonLat = ol.proj.toLonLat(c);
          return {lat: lonLat[1], lng: lonLat[0]};
        });
        this.coordinatesChange.emit(latlngs);
      });
    });

    this.drawInteraction.on('drawend', (event: any) => {
      this.isDrawing = false;
      this.map.removeInteraction(this.drawInteraction);


      this.enableEditPolygon();
      this.savePolygon(event?.feature)
    });

    this.map.addInteraction(this.drawInteraction);
  }

  savePolygon(feature: any) {
    if (!feature) return;
    const coords = feature.getGeometry().getCoordinates()[0];

    const firstCoord = coords[0];
    const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

    const realCoords = isProjected
      ? coords.map((coord: [number, number]) => ol.proj.toLonLat(coord))
      : coords;

    this.geometry = `POLYGON ((${realCoords
      .map((coord: [number, number]) => `${coord[0].toFixed(6)} ${coord[1].toFixed(6)}`)
      .join(', ')}))`;


    this.polygonAreaChange.emit(this.calculateAreaFromPolygon(JSON.stringify(realCoords)));

    this.geometryChange.emit(this.geometry);
  }


  public startLineStringDrawing() {
    this.isDrawingPolygon = false;
    this.isDrawingLineString = true;
    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
    }

    this.drawInteraction = new ol.interaction.Draw({
      source: this.vectorSource,
      type: 'LineString'
    });

    this.drawingMode = true;

    this.drawInteraction.on('drawstart', (event: any) => {
      const lineString = event.feature;
      lineString.set('type', 'lineString');

      lineString.getGeometry().on('change', (geomEvt: any) => {
        const coords = geomEvt.target.getCoordinates();

        this.vectorSource.getFeatures()
          .filter((f: any) => f.get('type') === 'vertex')
          .forEach((f: any) => this.vectorSource.removeFeature(f));

        coords.forEach((coord: any, index: number) => {
          const pointFeature = new ol.Feature({
            geometry: new ol.geom.Point(coord),
            type: 'vertex'
          });

          pointFeature.setStyle(new ol.style.Style({
            image: new ol.style.Circle({
              radius: 12,
              fill: new ol.style.Fill({
                color: '#ffffff'
              }),
              stroke: new ol.style.Stroke({
                color: '#ff0000',
                width: 2
              })
            }),
            text: new ol.style.Text({
              text: (index + 1).toString(),
              font: '14px Arial',
              fill: new ol.style.Fill({
                color: '#000000'
              }),
              stroke: new ol.style.Stroke({
                color: '#ffffff',
                width: 3
              }),
              offsetY: -17
            })
          }));

          this.vectorSource.addFeature(pointFeature);
        });

        const latlngs = coords.map((c: any) => {
          const lonLat = ol.proj.toLonLat(c);
          return {lat: lonLat[1], lng: lonLat[0]};
        });
        this.coordinatesChange.emit(latlngs);
      });
    });

    this.drawInteraction.on('drawend', (event: any) => {
      this.isDrawing = false;
      this.map.removeInteraction(this.drawInteraction);

      const geoJSONFormat = new ol.format.GeoJSON();
      const geoJSONGeometry: GeoJSONGeometry = geoJSONFormat.writeGeometryObject(event.feature.getGeometry()) as GeoJSONGeometry;
      this.geometryChange.emit(JSON.stringify(geoJSONGeometry));
    });

    this.map.addInteraction(this.drawInteraction);
  }

  public updateCenterMarker(lon: number, lat: number) {

    const view = this.map.getView();
    view.animate({
      center: ol.proj.fromLonLat([lon, lat]),
      duration: 250
    });

    if (this.map && typeof ol !== 'undefined') {
      if (this.centerMarker) {
        this.vectorSource.removeFeature(this.centerMarker);
      }

      const markerFeature = new ol.Feature({
        geometry: new ol.geom.Point(ol.proj.fromLonLat([lon, lat])),
        type: 'centerMarker'
      });

      if (this.isCenterMarker && this.showCenterMarkerOnInit) {

        markerFeature.setStyle(new ol.style.Style({
          image: new ol.style.Icon({
            anchor: [0.5, 1],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            src: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 2.902 1.004 5.57 2.68 7.682L12.5 41l9.82-20.818C23.996 18.07 25 15.402 25 12.5 25 5.596 19.404 0 12.5 0z" fill="#ff0000"/>
              <circle cx="12.5" cy="12.5" r="6" fill="white"/>
            </svg>
          `),
            scale: 1
          })
        }));

        this.vectorSource.addFeature(markerFeature);
        this.centerMarker = markerFeature;
      }

    }
  }

  public addSatelliteLayer() {
    if (!this.map || !ol) return;

    if (!this.satelliteLayer) {
      this.satelliteLayer = new ol.layer.Tile({
        source: new ol.source.OSM(),
        opacity: 1
      });
      const layers = this.map.getLayers();
      layers.insertAt(1, this.satelliteLayer);
    }
  }

  public removeSatelliteLayer() {
    if (this.map && this.satelliteLayer) {
      this.map.removeLayer(this.satelliteLayer);
      this.satelliteLayer = null;
    }
  }

  public toggleSatelliteLayer() {
    if (this.satelliteLayer) {
      this.removeSatelliteLayer();
    } else {
      this.addSatelliteLayer();
    }
  }


  ngOnDestroy() {
    this.stopPolygonDrawing();
    if (this.ndiLayer) {
      this.map.removeLayer(this.ndiLayer);
      this.ndiLayer = null;
    }
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }
  }

  public stopPolygonDrawing() {

    //
    // if (this.drawInteraction) {
    //   this.map.removeInteraction(this.drawInteraction);
    //   this.drawInteraction = null;
    // }
    //
    // if (this.vectorSource) {
    //   this.vectorSource.clear();
    // }

    this.drawingMode = false;
  }

  public removePolygonLayer() {
    if (this.vectorSource) {
      const featuresToRemove = this.vectorSource.getFeatures()
        .filter((feature: any) => feature.getGeometry().getType() === 'Polygon');

      featuresToRemove.forEach((feature: any) => {
        this.vectorSource.removeFeature(feature);
      });
    }

    if (this.drawInteraction) {
      this.map.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }

    this.drawingMode = false;
  }


  public zoomIn() {
    if (this.map) {
      const view = this.map.getView();
      const zoom = view.getZoom();
      view.animate({
        zoom: zoom + 1,
        duration: 250
      });
    }
  }

  public zoomOut() {
    if (this.map) {
      const view = this.map.getView();
      const zoom = view.getZoom();
      view.animate({
        zoom: zoom - 1,
        duration: 250
      });
    }
  }


  public centerMap() {
    if (this.map && this.center) {
      const view = this.map.getView();
      view.animate({
        center: ol.proj.fromLonLat(this.center),
        zoom: this.zoom,
        duration: 250
      });
    }
  }


  private parsePolygonJson(polygonJson: string): [number, number][] | null {
    if (!polygonJson || !polygonJson.startsWith('POLYGON')) {
      console.error('Invalid POLYGON format');
      return null;
    }

    try {
      const coordinatesString = polygonJson
        .replace('POLYGON ((', '')
        .replace('))', '');

      const coordinatePairs = coordinatesString.split(',');

      return coordinatePairs.map(pair => {
        const [lon, lat] = pair.trim().split(' ').map(Number);
        return [lon, lat] as [number, number];
      });
    } catch (error) {
      console.error('Error parsing POLYGON JSON:', error);
      return null;
    }
  }

  polygon3857to4326(rings3857: [number, number][][]): [number, number][][] {
    return rings3857.map(ring => ring.map(coord => new ol.proj.toLonLat(coord)));
  }


  drawPolygonFromJson(polygonJson: string, color?: string) {
    if (!polygonJson || !this.vectorSource || !this.map) return;

    const polygonCoordinates = this.parsePolygonJson(polygonJson);
    if (!polygonCoordinates || polygonCoordinates.length < 3) return;

    try {
      const firstCoord = polygonCoordinates[0];
      const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

      const transformedRing = isProjected
        ? polygonCoordinates
        : polygonCoordinates.map(coord => ol.proj.fromLonLat(coord));

      const polygonGeometry = new ol.geom.Polygon([transformedRing]);
      const extent = polygonGeometry.getExtent();

      if (!extent || extent.some((val: any) => !isFinite(val))) {
        console.error('Invalid polygon extent:', {polygonJson, extent});
        return;
      }

      const polygonFeature = new ol.Feature({geometry: polygonGeometry});
      color = color || this.polygonColor
      const borderColor = getBorderColorFromPolygonColor(color);
      polygonFeature.setStyle(new ol.style.Style({
        stroke: new ol.style.Stroke({color: borderColor, width: 2}),
        fill: new ol.style.Fill({color: color })
      }));

      this.vectorSource.addFeature(polygonFeature);

      this.map.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: 17
      });

      const initialArea = this.calculateAreaFromPolygon(polygonJson);
      this.polygonAreaChange.emit(initialArea);

    } catch (error) {
      console.error('Error drawing polygon:', error);
    }
  }


  private enableEditPolygon() {
    if (!this.drawingMode){
      return;
    }
    if (!this.vectorSource) return;
    if (this.modifyInteraction) {
      this.map.removeInteraction(this.modifyInteraction);
    }
    this.modifyInteraction = new ol.interaction.Modify({
      source: this.vectorSource
    });

    this.map.addInteraction(this.modifyInteraction);

    this.modifyInteraction.on('modifyend', (event: any) => {
      const features = event.features.getArray();
      features.forEach((feature: any) => {
        if (feature.getGeometry().getType() === 'Polygon') {
          this.savePolygon(feature)
        }
      });
    });
  }


  private calculateAreaFromPolygon(polygonJson: string): number {
    if (!polygonJson) return 0;

    try {

      let coordinates: [number, number][] = [];
      if (polygonJson.startsWith('POLYGON')) {
        coordinates = this.parsePolygonJson(polygonJson) ?? [];
      } else {
        coordinates = JSON.parse(polygonJson) as [number, number][];
      }

      if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 3) return 0;

      const firstCoord = coordinates[0];
      const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

      const realCoords = isProjected
        ? coordinates.map((coord: [number, number]) => ol.proj.toLonLat(coord))
        : coordinates;

      let area = 0;
      const n = realCoords.length - 1;

      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += realCoords[i][0] * realCoords[j][1];
        area -= realCoords[j][0] * realCoords[i][1];
      }

      area = Math.abs(area) / 2;

      const areaInSquareMeters = area * (111320 * 111320);

      const areaInHectares = areaInSquareMeters / 10000;

      return Number(areaInHectares.toFixed(2));

    } catch (error) {
      return 0;
    }
  }
}
